import { useEffect, useState } from 'react';
import { Outlet, Route, Routes, useNavigate, useParams } from 'react-router';
import { ApiError, errorMessage } from '../../api.js';
import { Icon } from '../../components/Icon.js';
import { Dialog, EmptyState, Notice } from '../../components/ui.js';
import { useOffline } from '../../offline/context.js';
import { useSignedIn } from '../../session.js';
import { loadContact, type ContactSheetData } from '../people/data.js';
import { CompleteContact, CompleteLead } from './Complete.js';
import { todoItems, type TodoItem } from './data.js';
import { useLead, usePeopleBack } from './LeadSheet.js';
import { ReviewCallWindow } from '../voice/ReviewCallWindow.js';

/**
 * To do (brief section 6.9), mounted at /todo/*: only what blocks the push,
 * the signed-in user's own contacts to correct and leads to document, each
 * with what is missing and its keyboard short form. Documented records leave
 * the list and go to HubSpot by themselves; by voice, the review call (step 5).
 */
export function TodoScreen() {
  return (
    <Routes>
      <Route element={<TodoList />}>
        <Route index element={null} />
        <Route path="lead/:id" element={<CompleteLeadWindow back="/todo" />} />
        <Route path="contact/:id" element={<CompleteContactWindow />} />
        <Route path="voice" element={<ReviewCallWindow />} />
        <Route path="voice/:kind/:id" element={<ReviewCallWindow />} />
      </Route>
    </Routes>
  );
}

function TodoList() {
  const { user, can } = useSignedIn();
  const voice = can('todo.voice');
  const { store, counts } = useOffline();
  const navigate = useNavigate();
  const [items, setItems] = useState<TodoItem[] | null>(null);

  useEffect(() => {
    if (!store) return;
    let current = true;
    void todoItems(store, user.id).then((list) => current && setItems(list));
    return () => {
      current = false;
    };
    // counts: a change saved or received refreshes the list.
  }, [store, user.id, counts]);

  const open = (item: TodoItem) =>
    navigate(item.kind === 'lead' ? `/people/leads/${item.id}` : `/people/contacts/${item.id}`, {
      state: { from: '/todo' },
    });

  return (
    <>
      <div className="page-head">
        <h1 className="page">To document</h1>
        {voice && items && items.length > 0 && (
          <div className="actions">
            <button
              type="button"
              className="btn sm primary"
              onClick={() => navigate('/todo/voice')}
            >
              <Icon name="mic" />
              Complete all by voice
            </button>
          </div>
        )}
      </div>
      <p className="lede">
        What is missing before it can go to HubSpot. Once documented, it goes by itself. Follow-up
        tasks are created in HubSpot.
      </p>
      {items && items.length === 0 && (
        <EmptyState title="Everything is documented.">It goes to HubSpot by itself.</EmptyState>
      )}
      {items && items.length > 0 && (
        <div className="list" aria-label="To document">
          {items.map((item) => (
            <div key={`${item.kind}:${item.id}`} className="todo-row">
              <button type="button" className="todo-main" onClick={() => open(item)}>
                <span className="t1">{item.title}</span>
                <span className="t2">{item.sub}</span>
                <span className="le-tags">
                  {item.missing.map((m) => (
                    <span key={m.field} className="tag warn">
                      {m.label}
                    </span>
                  ))}
                  {item.tasks.map((t) => (
                    <span key={t} className="tag">
                      {t}
                    </span>
                  ))}
                </span>
              </button>
              <span className="todo-act">
                <button
                  type="button"
                  className="btn sm"
                  aria-label={`Complete ${item.title} by keyboard`}
                  onClick={() => navigate(`/todo/${item.kind}/${item.id}`)}
                >
                  <Icon name="edit" />
                </button>
                {voice && (
                  <button
                    type="button"
                    className="btn sm primary"
                    aria-label={`Complete ${item.title} by voice`}
                    onClick={() => navigate(`/todo/voice/${item.kind}/${item.id}`)}
                  >
                    <Icon name="mic" />
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
      <Outlet />
    </>
  );
}

/** The keyboard short form of a lead, from To do or from the lead sheet. */
export function CompleteLeadWindow({ back }: { back?: string }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const people = usePeopleBack();
  const { data, error } = useLead(id);
  const done = () => (back ? navigate(back) : people.open(`/people/leads/${id}`));
  if (!data) {
    return (
      <Dialog title="Complete" onClose={done}>
        {error ? (
          <Notice kind="error">{error}</Notice>
        ) : (
          <div className="spinner" aria-label="Loading" />
        )}
      </Dialog>
    );
  }
  return <CompleteLead key={id} data={data} onDone={done} />;
}

function CompleteContactWindow() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useSignedIn();
  const { store } = useOffline();
  const [data, setData] = useState<ContactSheetData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const done = () => navigate('/todo');
  useEffect(() => {
    let current = true;
    loadContact(id, store, { id: user.id, name: `${user.firstName} ${user.lastName}` })
      .then((loaded) => {
        if (!current) return;
        if (loaded) setData(loaded);
        else setError('This contact does not exist, or it was deleted.');
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else if (current) setError(errorMessage(err));
      });
    return () => {
      current = false;
    };
  }, [id, store, user, refresh]);
  if (!data) {
    return (
      <Dialog title="Complete" onClose={done}>
        {error ? (
          <Notice kind="error">{error}</Notice>
        ) : (
          <div className="spinner" aria-label="Loading" />
        )}
      </Dialog>
    );
  }
  return <CompleteContact key={id} contact={data.contact} onDone={done} />;
}
